import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Award, Clock } from 'lucide-react';
import { examsApi, publicConfigApi, type HomepageSectionData } from '@/services/api';
import { getContentLocale, pickLocalizedText } from '@/lib/contentLocale';
import { ExamAffiliationNotice } from '@/components/ExamAffiliationNotice';
import { trackSearch, trackSuperCategorySelect } from '@/lib/track';

interface Certification {
  id: string;
  name: string;
  description: string;
  icon: string;
  bgGradient: string;
  level: string;
  duration: number;
  category: string;
  homepageSectionSlug: string | null;
  totalQuestions: number;
}

export function CertificationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [sections, setSections] = useState<HomepageSectionData[]>([]);
  const [loading, setLoading] = useState(true);
  const locale = getContentLocale();

  const selectedSection = searchParams.get('section') || null;
  const selectedCategory = searchParams.get('category') || null;

  const setSelectedSection = (slug: string | null) => {
    if (slug) {
      // Super-catégorie la plus utilisée (filtre par section).
      const sec = sections.find((s) => s.slug === slug);
      trackSuperCategorySelect(slug, sec?.title ?? slug, 'section_open');
    }
    const params = new URLSearchParams();
    if (slug) params.set('section', slug);
    setSearchParams(params, { replace: true });
  };

  const setSelectedCategory = (cat: string | null) => {
    const params = new URLSearchParams();
    if (selectedSection) params.set('section', selectedSection);
    if (cat) params.set('category', cat);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    Promise.all([
      examsApi.getAll(),
      publicConfigApi.getHomepageSections().catch(() => ({ data: [] as HomepageSectionData[] })),
    ]).then(([examsRes, sectionsRes]) => {
      const items: any[] = Array.isArray(examsRes.data) ? examsRes.data : examsRes.data.items ?? [];
      setCertifications(items.map((e: any) => ({
        id: e.id,
        name: e.name,
        description: e.description || '',
        icon: e.icon || '📚',
        bgGradient: e.bgGradient || 'from-emerald-500 to-teal-600',
        level: e.level || 'Intermédiaire',
        duration: e.duration || 60,
        category: e.category || 'Général',
        homepageSectionSlug: e.homepageSectionSlug ?? null,
        totalQuestions: e.totalQuestions || 0,
      })));
      setSections(sectionsRes.data.filter((s) => s.isVisible));
    }).catch(() => {
      setCertifications([]);
      setSections([]);
    }).finally(() => setLoading(false));
  }, []);

  const examsInSection = useMemo(() => {
    if (!selectedSection) return certifications;
    return certifications.filter((c) => c.homepageSectionSlug === selectedSection);
  }, [certifications, selectedSection]);

  const subCategories = useMemo(() => {
    const cats = new Map<string, number>();
    examsInSection.forEach((c) => {
      if (c.category) cats.set(c.category, (cats.get(c.category) || 0) + 1);
    });
    return Array.from(cats.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [examsInSection]);

  const filteredCertifications = useMemo(() => {
    return examsInSection.filter((cert) => {
      const matchesSearch = !searchQuery ||
        cert.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cert.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = !selectedCategory || cert.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [examsInSection, searchQuery, selectedCategory]);

  // Suivi GA4 des recherches (débounce 700ms) sur la page certifications.
  useEffect(() => {
    const term = searchQuery.trim();
    if (!term) return;
    const timer = setTimeout(() => trackSearch(term, filteredCertifications.length), 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const selectedSectionData = sections.find((s) => s.slug === selectedSection);
  const pageTitle = selectedSectionData
    ? pickLocalizedText(locale, selectedSectionData.title, selectedSectionData.titleEn)
    : (locale === 'en' ? 'Certifications' : 'Certifications');

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Débutant': return 'bg-green-500/15 text-green-400 border border-green-500/20';
      case 'Intermédiaire': return 'bg-blue-500/15 text-blue-400 border border-blue-500/20';
      case 'Avancé': return 'bg-orange-500/15 text-orange-400 border border-orange-500/20';
      case 'Expert': return 'bg-red-500/15 text-red-400 border border-red-500/20';
      default: return 'bg-overlay-2 text-fg-subtle';
    }
  };

  const t = locale === 'en' ? {
    all: 'All',
    search: 'Search a certification...',
    noResults: 'No certifications found',
    allCategories: 'All categories',
  } : {
    all: 'Tous',
    search: 'Rechercher une certification...',
    noResults: 'Aucune certification trouvée',
    allCategories: 'Toutes les catégories',
  };

  return (
    <div className="flex flex-col h-full bg-app pb-20 lg:pb-0">
      {/* Header + Search */}
      <div className="px-4 sm:px-5 lg:px-6 pt-5 sm:pt-6 pb-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-fg mb-1.5 sm:mb-2">{pageTitle}</h1>
          <div className="mb-3 sm:mb-4">
            <ExamAffiliationNotice variant="banner" />
          </div>
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-faint" />
            <input
              type="text"
              placeholder={t.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-overlay-1 border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-sm transition-all"
            />
          </div>
        </div>
      </div>

      {/* Row 1: Super categories */}
      {sections.length > 0 && (
        <div className="px-4 sm:px-5 lg:px-6 pb-2 flex-shrink-0">
          <div className="max-w-7xl mx-auto">
            <div className="flex gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide pb-1">
              <button
                onClick={() => setSelectedSection(null)}
                className={`px-3 sm:px-4 py-1.5 rounded-lg text-[11px] sm:text-xs font-medium whitespace-nowrap transition-colors ${
                  !selectedSection
                    ? 'bg-green-500/20 text-green-400 border border-green-500/20'
                    : 'bg-overlay-1 text-fg-subtle border border-line hover:text-fg-muted'
                }`}
              >
                {t.all}
                <span className="ml-1 opacity-60">({certifications.length})</span>
              </button>
              {sections.map((s) => {
                const count = certifications.filter((c) => c.homepageSectionSlug === s.slug).length;
                const isActive = selectedSection === s.slug;
                return (
                  <button
                    key={s.slug}
                    onClick={() => setSelectedSection(s.slug)}
                    className={`px-3 sm:px-4 py-1.5 rounded-lg text-[11px] sm:text-xs font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-green-500/20 text-green-400 border border-green-500/20'
                        : 'bg-overlay-1 text-fg-subtle border border-line hover:text-fg-muted'
                    }`}
                  >
                    {s.icon && <span className="mr-1">{s.icon}</span>}
                    {pickLocalizedText(locale, s.title, s.titleEn)}
                    <span className="ml-1 opacity-60">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Row 2: Sub-categories (only when a super category is selected) */}
      {selectedSection && subCategories.length > 1 && (
        <div className="px-4 sm:px-5 lg:px-6 pb-3 flex-shrink-0">
          <div className="max-w-7xl mx-auto">
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-2.5 sm:px-3 py-1 rounded-md text-[10px] sm:text-[11px] font-medium whitespace-nowrap transition-colors ${
                  !selectedCategory
                    ? 'bg-violet-500/20 text-violet-400 border border-violet-500/20'
                    : 'bg-overlay-1 text-fg-faint border border-line hover:text-fg-subtle'
                }`}
              >
                {t.allCategories}
                <span className="ml-1 opacity-60">({examsInSection.length})</span>
              </button>
              {subCategories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => setSelectedCategory(cat.name)}
                  className={`px-2.5 sm:px-3 py-1 rounded-md text-[10px] sm:text-[11px] font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === cat.name
                      ? 'bg-violet-500/20 text-violet-400 border border-violet-500/20'
                      : 'bg-overlay-1 text-fg-faint border border-line hover:text-fg-subtle'
                  }`}
                >
                  {cat.name}
                  <span className="ml-1 opacity-60">({cat.count})</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-5 lg:px-6 pb-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs text-fg-faint mb-3">
            {filteredCertifications.length} certification{filteredCertifications.length > 1 ? 's' : ''}
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredCertifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-fg-faint">
              <Award className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-sm font-medium">{t.noResults}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredCertifications.map((cert) => (
                <button
                  key={cert.id}
                  onClick={() => navigate(`/exam/${cert.id}`)}
                  className="group text-left bg-surface border border-line rounded-2xl p-4 hover:border-line transition-all"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br ${cert.bgGradient} rounded-xl flex items-center justify-center text-lg sm:text-xl flex-shrink-0`}>
                      {cert.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-xs sm:text-sm font-semibold text-fg line-clamp-2 leading-snug">{cert.name}</h3>
                      <p className="text-[10px] sm:text-xs text-fg-faint mt-0.5">{cert.category}</p>
                    </div>
                  </div>
                  <p className="text-[10px] sm:text-xs text-fg-faint line-clamp-2 mb-3 leading-relaxed">{cert.description}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded-md ${getLevelColor(cert.level)}`}>{cert.level}</span>
                    <span className="text-[10px] text-fg-faint flex items-center gap-1">
                      <Clock className="w-3 h-3" />{cert.duration} min
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
